/**
 * NotificationBell — bell trigger + unread badge + dropdown panel.
 *
 * Lives in the (Forest) NavBar: the trigger uses palette tokens (`text-cream`)
 * to sit on the dark bar; the panel uses semantic tokens so it adapts to
 * light/dark just like the user avatar dropdown next to it.
 *
 * Shows ordinary account items only. Channel Partner items live in the CP Hub
 * bell; the account-scoped Hub summary item links there.
 */
import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Bell, CheckCheck } from 'lucide-react';
import { cn } from '@finmin/ui';
import { useNotifications } from '../notifications-context';
import { getEventMeta } from '../event-meta';
import { formatRelativeTime } from '../utils/format-relative-time';
import { CHANNEL_PARTNER_HUB_PATH, CP_DASHBOARD_SUMMARY_EVENT } from '../scope';
import type { AppNotification } from '../types';

/** First non-empty deep-link id we can resolve from a notification's data map. */
function resolveServiceRequestId(n: AppNotification): string | null {
  const id = n.data?.service_request_id ?? n.data?.request_id;
  return id ? String(id) : null;
}

export function NotificationBell() {
  const navigate = useNavigate();
  const { notifications, unreadCount, status, markAsRead, markAllAsRead } = useNotifications();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  // Close on outside click.
  React.useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', handleOutside);
      return () => document.removeEventListener('mousedown', handleOutside);
    }
  }, [open]);

  function handleItemClick(n: AppNotification) {
    if (!n.read) markAsRead(n.id);
    if (n.event === CP_DASHBOARD_SUMMARY_EVENT) {
      setOpen(false);
      void navigate({ to: CHANNEL_PARTNER_HUB_PATH });
      return;
    }
    const requestId = resolveServiceRequestId(n);
    if (requestId) {
      setOpen(false);
      void navigate({ to: '/service-request/$id', params: { id: requestId } });
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-haspopup="true"
        aria-expanded={open}
        className="relative flex h-9 w-9 items-center justify-center rounded-lg text-cream transition-colors hover:bg-cream/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Bell className="h-5 w-5" aria-hidden />
        {unreadCount > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold leading-none text-accent-foreground"
            aria-hidden
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Mobile-only backdrop: dims the page and taps to close. */}
          <div
            className="fixed inset-0 z-40 bg-hero/30 sm:hidden"
            aria-hidden
            onClick={() => setOpen(false)}
          />

          <div
            className={cn(
              // Mobile: fixed, near-full-width sheet just below the navbar (h-16).
              'fixed inset-x-2 top-16 z-50 mt-2 overflow-hidden rounded-lg border border-border bg-background shadow-lg animate-slide-down-fade',
              // Desktop (sm+): compact dropdown anchored under the bell.
              'sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:w-96',
            )}
            role="menu"
          >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
            <p className="text-sm font-semibold text-foreground">Notifications</p>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllAsRead('account')}
                className="inline-flex items-center gap-1 text-xs font-medium text-accent transition-opacity hover:opacity-80"
              >
                <CheckCheck className="h-3.5 w-3.5" aria-hidden />
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[70vh] overflow-y-auto overscroll-contain sm:max-h-96">
            {status === 'error' ? (
              <div className="flex flex-col items-center justify-center gap-1 px-4 py-10 text-center">
                <p className="text-sm font-medium text-foreground">Couldn&apos;t load notifications</p>
                <p className="text-xs text-muted-foreground">
                  Please refresh. If this persists, check your connection.
                </p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
                <Bell className="h-7 w-7 text-muted-foreground/50" aria-hidden />
                <p className="text-sm text-muted-foreground">You&apos;re all caught up</p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {notifications.map((n) => {
                  const { Icon } = getEventMeta(n.event);
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => handleItemClick(n)}
                        className={cn(
                          'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted',
                          !n.read && 'bg-accent/5',
                        )}
                      >
                        <span
                          className={cn(
                            'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                            n.read ? 'bg-muted text-muted-foreground' : 'bg-accent/15 text-accent',
                          )}
                        >
                          <Icon className="h-4 w-4" aria-hidden />
                        </span>

                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className="truncate text-sm font-semibold text-foreground">
                              {n.title || 'Notification'}
                            </span>
                            {!n.read && (
                              <span
                                className="ml-auto h-2 w-2 shrink-0 rounded-full bg-accent"
                                aria-hidden
                              />
                            )}
                          </span>
                          {n.body && (
                            <span className="mt-0.5 block text-xs text-muted-foreground line-clamp-2">
                              {n.body}
                            </span>
                          )}
                          <span className="mt-1 block text-[11px] text-muted-foreground/70">
                            {formatRelativeTime(n.createdAt)}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          </div>
        </>
      )}
    </div>
  );
}
